---
title: "Gage Technologies at IAUG Converge 2026"
keywords: "IAUG Converge 2026, Avaya user group, Gage Technologies, Gold Sponsor, Savannah, unified communications, contact center, exhibitor, breakout sessions, Avaya Infinity, ASBCE certificate automation"
seo:
  title: "Gage at IAUG Converge 2026 | Gold Sponsor & Exhibitor"
  description: "Gage is a Gold Sponsor at IAUG Converge 2026 in Savannah, Georgia. Visit our booth for UC, contact center, AI, and network conversations, and catch our breakout sessions on Avaya Infinity and ASBCE certificate automation."
  image: "/img/converge2026/savannah-oaks.webp"
  twitter:
    card: "summary_large_image"
    title: "Gage at IAUG Converge 2026 | Gold Sponsor & Exhibitor"
    description: "Join Gage Technologies at IAUG Converge 2026 in Savannah, Georgia. Gold Sponsor and Exhibitor, with breakout sessions on Avaya Infinity and ASBCE certificate automation."
    image: "/img/converge2026/savannah-oaks.webp"
---

<PotsAlertBar>
  <strong>November 2-4, 2026</strong> | Savannah Convention Center | Savannah, Georgia <a href="https://www.prereg.net/2026/iaug" target="_blank" rel="noopener">Register for Converge 2026</a>
</PotsAlertBar>

<PotsHero
  title="Gage Technologies at IAUG Converge 2026"
  description="The customer-first communication event is back. Gage is returning as a Gold Sponsor and Exhibitor at IAUG Converge 2026 in Savannah, Georgia. Stop by our booth to talk UC, contact center, AI, or network infrastructure with senior engineers who've been in the field for 20+ years."
  ctaText="Register for Converge"
  ctaHref="https://www.prereg.net/2026/iaug"
  ctaSubtext="Use GAGE150 for $150 off"
/>

<div class="converge-city-banner">
  <img src="/img/converge2026/savannah-riverfront-sunset.webp" alt="Savannah riverfront at dusk with the Georgia Queen paddleboat and City Hall gold dome" />
  <div class="converge-city-banner-text">
    <p class="city-name">Savannah, Georgia</p>
    <p class="city-tagline">November 2 to 4, 2026 | Savannah Convention Center</p>
  </div>
  <span class="converge-city-banner-credit">Photo: Unsplash</span>
</div>

<Section>
  <PotsEyebrow>Why We're Back</PotsEyebrow>
  ## Returning to Savannah as Gold Sponsor

  Last year at Converge 2025 in Kansas City, the Gage team spent three days talking with Avaya users, partners, and IT leaders from across the country. The conversations were direct. People told us what they were dealing with: migrations, AI adoption pressure, contact center upgrades, and the usual mess of legacy infrastructure that nobody has time to fix.

  That's why we're coming back, and why we stepped up to Gold Sponsor. This year, Gage returns to IAUG Converge as a **Gold Sponsor and Exhibitor**, and we want to pick up those conversations where they left off. We're an outcome-based systems integrator with 20+ years and 500+ clients behind us. The perspective we bring on Avaya environments is from the field, not a slide deck.

  <PotsCallout variant="accent">
    <p><strong>Gold Sponsor and Exhibitor.</strong> Visit the Gage booth to talk with senior engineers about your UC, contact center, AI, and network infrastructure goals. No sales pitch, no badge scan quota. Just people who've done the work.</p>
  </PotsCallout>
</Section>

<Section data-gray>
  <StatBar>
    <Stat value="3" label="Days of sessions and networking" />
    <Stat value="Gold" label="Sponsor level at Converge 2026" />
    <Stat value="500+" label="Attendees at Converge" />
    <Stat value="20+" label="Years in the field" />
  </StatBar>
</Section>

<Section>
  <PotsEyebrow>The Venue</PotsEyebrow>
  ## Savannah Convention Center

  IAUG Converge 2026 is at the Savannah Convention Center, overlooking the Savannah River in historic downtown Savannah, Georgia. The conference hotel is the Savannah Westin, connected to the convention center. The venue has expo halls, breakout rooms for technical tracks, and banquet space for keynotes and networking.

  <div class="converge-venue-grid">
    <div class="converge-venue-item">
      <img src="/img/converge2026/linkedin-welcome-banner.webp" alt="IAUG Converge welcome banner at the venue entrance" />
    </div>
    <div class="converge-venue-item">
      <img src="/img/converge2026/iaug-breakout-session.webp" alt="Breakout session at IAUG Converge with audience" />
    </div>
    <div class="converge-venue-item">
      <img src="/img/converge2026/linkedin-keynote-session.webp" alt="Keynote session at IAUG Converge with full audience" />
    </div>
    <div class="converge-venue-item">
      <img src="/img/converge2026/iaug-heart-sculpture.webp" alt="Heartland art display at the IAUG Converge expo hall" />
    </div>
  </div>

  <PotsCallout>
    <p><strong>Conference hotel:</strong> Savannah Westin Hotel, attached to the Convention Center. Book early through the IAUG registration portal for the group rate.</p>
  </PotsCallout>
</Section>

<Section data-gray>
  <PotsEyebrow>At the Gage Booth</PotsEyebrow>
  ## What to Expect When You Visit Us

  Stop by the Gage booth and talk with senior engineers and solutions architects about your specific environment. We're not there to push products. We want to hear what you're dealing with and share what we're seeing in the field. We've helped other organizations through the same decisions.

  <PotsCards>
    <PotsCard
      icon="phone"
      title="Unified Communications Expertise"
      description="Avaya UC migrations, upgrades, and optimization. On-prem, cloud, or hybrid. We help you get there without breaking what's already working."
    />
    <PotsCard
      icon="users"
      title="Contact Center Solutions"
      description="AI-powered routing, workforce engagement, and omnichannel capabilities. Improve agent experience and customer outcomes without ripping out everything you already own."
    />
    <PotsCard
      icon="trending"
      title="AI Readiness"
      description="Practical AI adoption for voice, UC, and contact center environments. Where AI actually helps, where it doesn't, and what your infrastructure needs to handle before you deploy it."
    />
    <PotsCard
      icon="shield"
      title="Network Infrastructure and Security"
      description="Network design, cybersecurity, and cloud solutions. We keep your communications infrastructure locked down and running, so you don't have to worry about it at 2am."
    />
  </PotsCards>

  <div class="pots-inline-cta">
    <ButtonCTA href="#contact" class="themed">Schedule a Meeting at Converge</ButtonCTA>
  </div>
</Section>

<Section>
  <PotsEyebrow>The AI Readiness Gap</PotsEyebrow>
  ## When the pressure to adopt AI outpaces your infrastructure

  Gartner found that 91% of customer service leaders are under executive pressure to implement AI in 2026. They also predict 60% of those projects will be abandoned, because the data and infrastructure underneath can't support them yet.

  If your communications platform is the foundation, and the foundation isn't ready for AI, executive pressure won't fix that. That's what we want to talk about at the Gage booth. You already know you need AI. You're hearing it from your leadership. What we can help you figure out is whether your infrastructure can actually handle it.

  <PotsCallout>
    <p><strong>Source:</strong> Gartner press releases, <a href="https://www.gartner.com/en/newsroom/press-releases/2026-02-18-gartner-survey-finds-ninety-one-percent-of-customer-service-leaders-under-pressure-to-implement-ai-in-2026" target="_blank" rel="noopener">February 2026</a> and <a href="https://www.gartner.com/en/newsroom/press-releases/2025-02-26-lack-of-ai-ready-data-puts-ai-projects-at-risk" target="_blank" rel="noopener">February 2025</a>.</p>
  </PotsCallout>
</Section>

<div class="converge-city-banner">
  <img src="/img/converge2026/savannah-oaks.webp" alt="Historic live oak avenue with Spanish moss at Wormsloe, near Savannah" />
  <span class="converge-city-banner-credit">Photo: Unsplash</span>
</div>

<Section>
  <PotsEyebrow>Event Highlights</PotsEyebrow>
  ## Three Days of Learning and Networking

  IAUG Converge 2026 runs Monday through Wednesday with general sessions, breakout tracks, hands-on labs, and expo hall time. Here's the schedule at a glance.

  <Grid maxGrid={3}>
    <div class="converge-day-card">
      <div class="converge-day-label">Monday</div>
      <h3 class="converge-day-title">November 2</h3>
      <ul class="converge-day-list">
        <li>Registration and check-in</li>
        <li>Breakout sessions begin</li>
        <li class="converge-day-gage">Gage session: Avaya Infinity, 3:40 to 4:40 PM, Room 204</li>
        <li>Welcome toast and reception</li>
      </ul>
    </div>
    <div class="converge-day-card">
      <div class="converge-day-label">Tuesday</div>
      <h3 class="converge-day-title">November 3</h3>
      <ul class="converge-day-list">
        <li>Breakfast and general session</li>
        <li>Breakout tracks and hands-on labs</li>
        <li class="converge-day-gage">Gage session: ASBCE certificate automation, 10:00 to 10:45 AM, Room 203</li>
        <li>Expo hall opens</li>
        <li>Evening reception</li>
        <li class="converge-day-gage">Gage After Party, 7:00 to 9:00 PM, Sorry Charlie's Bamboo Room</li>
      </ul>
    </div>
    <div class="converge-day-card">
      <div class="converge-day-label">Wednesday</div>
      <h3 class="converge-day-title">November 4</h3>
      <ul class="converge-day-list">
        <li>Breakfast and general session</li>
        <li class="converge-day-gage">Gage mainstage panel: Architecting the Future</li>
        <li>Breakout tracks and hands-on labs</li>
        <li class="converge-day-gage">Gage session: AI strategic blueprint, 3:55 to 4:55 PM, Room 201</li>
        <li>Expo hall open</li>
        <li>Passport to Prizes and closing party</li>
      </ul>
    </div>
  </Grid>

  <PotsCallout variant="accent">
    <p><strong>Tuesday night:</strong> The Gage After Party takes over the Bamboo Room at Sorry Charlie's, 7:00 to 9:00 PM. Food and drinks on us, and everyone at Converge is welcome. <a href="/converge-afterparty">Register for the after party</a>. Space is limited.</p>
  </PotsCallout>

  <PotsEyebrow>Conference Tracks</PotsEyebrow>
  ### Topics Covered Across the Event

  <div class="converge-tracks">
    <span class="converge-track">Contact Center Management</span>
    <span class="converge-track">UC Best Practices</span>
    <span class="converge-track">Employee Management and Efficiency</span>
    <span class="converge-track">Migration and Upgrades</span>
    <span class="converge-track">AI in Voice, UC, and Contact Center</span>
    <span class="converge-track">Security</span>
    <span class="converge-track">Networking</span>
    <span class="converge-track">Hands-On Labs</span>
  </div>

  <PotsCallout>
    <p><strong>Full agenda and session details:</strong> Visit the <a href="https://iaug.org/converge/" target="_blank" rel="noopener">IAUG Converge website</a> for the complete schedule, speaker list, and track descriptions.</p>
  </PotsCallout>
</Section>

<Section data-gray>
  <PotsEyebrow>Gage Sessions at Converge</PotsEyebrow>
  ## Three breakout sessions from Gage engineers

  Gage engineers are presenting three breakout sessions this year: one on Avaya Infinity, one on automating certificate lifecycle on the Avaya Session Border Controller for Enterprises (ASBCE), and Greg Hatch's closing AI blueprint. Add them to your agenda, then find the speakers at the booth afterward.

  <Boxes>
    <Box>
      <div class="converge-speaker">
        <img class="converge-speaker-photo" src="/img/converge2026/hatch-headshot.webp" alt="Greg Hatch" />
        <h3 class="converge-speaker-name">Greg Hatch</h3>
        <p class="converge-speaker-tagline">SVP, AI and Emerging Technology, Gage Technologies</p>
        <div class="converge-session">
          <p class="converge-session-title">Implementing AI: A Strategic Blueprint for Success</p>
          <p class="converge-session-meta">Wednesday, November 4 | 3:55 to 4:55 PM | Meeting Room 201</p>
          <p class="converge-session-desc">Why over 80% of enterprise AI projects fail to deliver business value, and a working blueprint for closing that gap: proven patterns from voice and customer experience, human adoption frameworks, data and metrics, and day-two support. Greg also joins the mainstage panel Architecting the Future earlier that morning. Leave with role-specific checklists for your next move with AI.</p>
        </div>
      </div>
    </Box>
    <Box>
      <div class="converge-speaker">
        <img class="converge-speaker-photo" src="/img/converge2026/zucchetto-headshot.webp" alt="J Zucchetto" />
        <h3 class="converge-speaker-name">J Zucchetto</h3>
        <p class="converge-speaker-tagline">Director, Technical Pre-Sales Engineering, Gage Technologies</p>
        <div class="converge-session">
          <p class="converge-session-title">Avaya Infinity: Unlocking Value with the Infinity Partner Instance</p>
          <p class="converge-session-meta">Monday, November 2 | 3:40 to 4:40 PM | Meeting Room 204</p>
          <p class="converge-session-desc">What the Avaya Infinity partner instance is and why it matters: how a partner instance supports proof-of-concept demos, enablement, and solution validation, and how it fits contact centers running under 200 seats. If you are evaluating Infinity for your environment, this is the session to bring your questions to.</p>
        </div>
      </div>
    </Box>
    <Box>
      <div class="converge-speaker">
        <img class="converge-speaker-photo" src="/img/converge2026/carroll-headshot.webp" alt="Randy Carroll" />
        <h3 class="converge-speaker-name">Randy Carroll</h3>
        <p class="converge-speaker-tagline">Solutions Engineer, Gage Technologies</p>
        <div class="converge-session">
          <p class="converge-session-title">ASBCE Certificate Lifecycle Automation: Surviving the 47-Day Validity Mandate</p>
          <p class="converge-session-meta">Tuesday, November 3 | 10:00 to 10:45 AM | Meeting Room 203</p>
          <p class="converge-session-desc">The CA/Browser Forum is phasing public TLS certificate validity down to 47 days by March 2029. On an ASBCE with internet-facing SIP trunks or remote worker endpoints, that adds up to as many as 8 renewals per year, per certificate. Randy, co-presenting with J Zucchetto, covers automating the ASBCE certificate lifecycle so renewals stop being a manual scramble. Rated Advanced / Technical Deep Dive.</p>
        </div>
      </div>
    </Box>
  </Boxes>

  <p class="converge-session-note">Times and rooms are from the IAUG agenda. Session schedules can shift, so confirm in the official agenda or event app when you build your schedule.</p>
</Section>

<Section>
  <PotsEyebrow>About Gage Technologies</PotsEyebrow>
  ## Outcome-Based Systems Integrator

  Gage is an outcome-based systems integrator with deep Avaya expertise in enterprise communications and customer experience. Its engineers connect contact centers and collaboration tools with the applications, data and network infrastructure behind them. The work starts with understanding the customer's business and continues through implementation and ongoing support.

  At Converge, Gage's focus is Avaya Infinity and practical AI applications that improve self-service and give agents better information during customer interactions. The team works alongside customers to determine how those capabilities fit their existing environments. Through professional and managed services, Gage helps customers put their plans into operation and stays accountable for how the technology works long after deployment.
</Section>

<Section id="contact">
  <ContactForm>
    ## Meet With Gage at<br/>IAUG Converge 2026

    <p class="pots-contact-subcopy" style="text-align: center; margin-left: auto; margin-right: auto;">Tell us what you're working on. UC migration, contact center modernization, AI adoption, network security, or something else entirely. We'll be at Converge and would like to meet. Schedule a time at the Gage booth, or just stop by.</p>
  </ContactForm>
</Section>
